import {
    createBrowserHistory,
    type BrowserHistoryBuildOptions,
    type History,
    type Location,
    type LocationDescriptor,
    type LocationDescriptorObject,
} from 'history';
import { omit } from 'lodash';

export interface CustomHistoryState {
    /**
     * A stack of possible return locations is automatically maintained by the custom history
     */
    returnLocationStack?: { normalizedPath: string; location: Omit<Location, 'state'> }[];

    /**
     * The return location can be explicitly set when pushing a new location. If set to true, the return location will be automatically determined as the previous location. If set to a location, it will be used as the return location.
     */
    returnLocation?: boolean | string | Omit<Location, 'state'>;
}

export default function createCustomHistory(options?: BrowserHistoryBuildOptions): History<CustomHistoryState> {
    const history = createBrowserHistory<CustomHistoryState>(options);
    const originalPush = history.push;
    const originalReplace = history.replace;

    history.push = (location, state) => {
        originalPush.call(history, updateReturnLocation('push', location, state));
    };

    history.replace = (location, state) => {
        originalReplace.call(history, updateReturnLocation('replace', location, state));
    };

    function updateReturnLocation(
        type: 'push' | 'replace',
        location: LocationDescriptor<CustomHistoryState>,
        state: CustomHistoryState | undefined,
    ): LocationDescriptorObject<CustomHistoryState> {
        let returnLocationStack = [...(history.location.state?.returnLocationStack ?? []), ...(state?.returnLocationStack ?? [])];
        let returnLocation = state?.returnLocation;

        state = {
            ...(typeof location === 'object' ? location.state : undefined),
            ...state,
        };

        if (type === 'push') {
            const newPath = normalizePath(typeof location === 'string' ? location : (location.pathname ?? history.location.pathname));
            const oldPath = normalizePath(history.location.pathname);

            returnLocationStack.push({
                normalizedPath: oldPath,
                location: omit(history.location, 'state'),
            });

            const index = returnLocationStack.findIndex((entry) => entry.normalizedPath.startsWith(newPath));
            if (index >= 0) {
                returnLocationStack = returnLocationStack.slice(0, index);
            }

            if (returnLocation === true) {
                returnLocation = omit(history.location, 'state');
            } else if (typeof returnLocation === 'string') {
                const url = new URL(returnLocation, window.location.href);
                returnLocation = {
                    pathname: url.pathname,
                    search: url.search,
                    hash: url.hash,
                };
            }
        } else {
            returnLocation ??= history.location.state?.returnLocation;
        }

        if (typeof location === 'string') {
            const url = new URL(location, window.location.href);
            location = {
                pathname: url.pathname,
                search: url.search,
                hash: url.hash,
            };
        }

        return {
            ...location,
            state: {
                ...state,
                returnLocationStack,
                returnLocation,
            },
        };
    }

    Object.assign(window, { _history: history });
    return history;
}

export function normalizePath(path: string) {
    path = new URL(path, window.location.href).pathname;

    if (!path.endsWith('/')) {
        path += '/';
    }

    return path;
}
